import { animate, AUTO_STYLE, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { CanColor, mixinColor } from '@angular/material/core';
import { Constructor } from '@mtna/core-ts';
import { COMPLEX_STANDARD, MtnaErrorService } from '@mtna/core-angular';
import { ProcessStatusDetail } from '@rds/rds-base-ts';
import { interval, Observable, Subscription } from 'rxjs';
import { catchError, concatMap, finalize, takeWhile, tap } from 'rxjs/operators';

export class RdsProcessesManagerBase {
  constructor(public _elementRef: ElementRef) {}
}

export const _RdsProcessManagerMixinBase: Constructor<CanColor> & typeof RdsProcessesManagerBase = mixinColor(
  RdsProcessesManagerBase,
  'primary'
);

export const RDS_PROCESSES_DEFAULT_HEADING_TEXT = 'Process details';
export const RDS_PROCESSES_DEFAULT_HEADING_DESCRIPTION = `All processes will be displayed below.`;

export class RdsProcessManagerData {
  /**
   * Creates new data for RdsProcessManagerComponent bottom sheet
   * @param incompleteProcessIds IDs for incomplete processes to poll
   * @param completeProcesses complete process details to display
   * @param headingText bottom sheet heading title
   * @param headingDescription bottom sheet heading description
   */
  constructor(
    public getProcessDetails: (processId: string) => Observable<ProcessStatusDetail>,
    public incompleteProcessIds: Array<string> = [],
    public completeProcesses: Array<ProcessStatusDetail> = [],
    public processDownloadUrl?: string,
    public headingText: string = RDS_PROCESSES_DEFAULT_HEADING_TEXT,
    public headingDescription: string = RDS_PROCESSES_DEFAULT_HEADING_DESCRIPTION
  ) {}
}

/**
 * MatBottomSheet that displays RDS processes.
 * Will show multiple processes, their progress, and the ability to download the result upon completion (if enabled).
 * Will automatically query the statuses every 600ms while there are any incomplete.
 *
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-c-process-manager',
  templateUrl: './process-manager.component.html',
  styleUrls: ['./process-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('expandInOut', [
      state('in', style({ height: AUTO_STYLE })),
      transition('void => *', [style({ height: '0px', overflow: 'hidden' }), animate(COMPLEX_STANDARD)]),
      transition('* => void', [
        style({ height: AUTO_STYLE, opacity: 1, overflow: 'hidden' }),
        animate(COMPLEX_STANDARD, style({ height: 0, opacity: 0 })),
      ]),
    ]),
  ],
})
export class RdsProcessManagerComponent extends _RdsProcessManagerMixinBase implements OnDestroy, OnInit {
  /** Emitted when a process finishes */
  @Output() processFinished = new EventEmitter<ProcessStatusDetail>();

  /** Theme color, defaults to primary */
  color: 'primary' | 'accent' = 'primary';
  /** List of process details that will be displayed */
  processDetails = new Array<ProcessStatusDetail>();
  /** Ids currently being polled */
  _pollingProcessIds = new Array<string>();

  _TEXT_close = $localize`Close`;

  /** Subscription for all the polling processes */
  private _pollingSub = new Subscription();

  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: RdsProcessManagerData,
    public bottomSheetRef: MatBottomSheetRef<RdsProcessManagerComponent>,
    elementRef: ElementRef,
    private errorService: MtnaErrorService,
    private cdr: ChangeDetectorRef
  ) {
    super(elementRef);
  }

  ngOnDestroy() {
    this._pollingSub.unsubscribe();
  }

  ngOnInit(): void {
    // Start polling incomplete processes
    if (this.data.incompleteProcessIds && this.data.incompleteProcessIds.length) {
      this.data.incompleteProcessIds.forEach((id) => this._addProcessPoll(id, 600));
    }
    // Add completed processed to the process details list
    if (this.data.completeProcesses && this.data.completeProcesses.length) {
      this.processDetails = [...this.processDetails, ...this.data.completeProcesses];
    }
  }

  /**
   * Start polling a process ID. Will unsubscribe when the process completes.
   * @param processId ID of the process to poll
   * @param pollInterval interval for polling in milliseconds, defaults to 600ms
   */
  private _addProcessPoll(processId: string, pollInterval: number = 600) {
    // Add ID to the list of polling IDs
    this._pollingProcessIds = [...this._pollingProcessIds, processId];
    this.cdr.markForCheck();

    // Start Polling
    this._pollingSub.add(
      // Subscribe to the interval operator
      interval(pollInterval)
        .pipe(
          // Get the process details on each interval
          concatMap((_) => this.data.getProcessDetails(processId)),
          tap((processStatus: ProcessStatusDetail) => {
            // Check whether the process already exists in the process details array
            const existingDetails = this.processDetails.find((details) => details.id === processStatus.id);
            if (!existingDetails) {
              // If it doesn't exist, add it to the array
              this.processDetails = [...this.processDetails, processStatus];
            } else {
              // If it already exists, update that process detail in the array
              this.processDetails = this.processDetails.map((details) => (details.id === processStatus.id ? processStatus : details));
            }

            // Check whether the process is finished
            if (this._isFinished(processStatus)) {
              // Emit the finishes ProcessStatusDetail for external use
              this.processFinished.emit(processStatus);
            }

            // The process details array and the incomplete processes array could have changed
            this.cdr.markForCheck();
          }),
          catchError((err) => {
            this.errorService.showError(err);
            throw Error(err);
          }),
          // Unsubscribe once the process is complete
          takeWhile((processStatus: ProcessStatusDetail) => this._isIncomplete(processStatus)),
          finalize(() => {
            // Once the subscription completes,
            // remove the ID from the list of polling IDs
            this._pollingProcessIds = this._pollingProcessIds.filter((id) => id !== processId);
            this.cdr.markForCheck();
          })
        )
        .subscribe()
    );
  }

  /**
   * @param psd process status to check
   * @returns Whether the process is finished
   */
  private _isFinished(psd: ProcessStatusDetail): boolean {
    return psd.status === 'COMPLETED' || psd.status === 'CANCELLED' || psd.status === 'FAILED';
  }

  /**
   * @param psd process status to check
   * @returns Whether the process is incomplete
   */
  private _isIncomplete(psd: ProcessStatusDetail): boolean {
    return psd.status === 'QUEUED' || psd.status === 'PROCESSING';
  }
}
